<script setup lang="ts">
import type {TodayRow, TodayTileKind} from '@/ts/types/component/today-page';
import {useInViewOnce} from '@/ts/composables/stateful/use-in-view-once';

defineProps<{
  rows: TodayRow[];
}>();

/**
 * A tile that reached the screen and stayed there long enough to have been looked at (IPD-2974).
 *
 * Announced from here rather than measured here: the cells are this component's own elements, so
 * it is the only place that can watch them, but what the event means and what else it carries is
 * the page's business.
 */
const emit = defineEmits<{tileSeen: [kind: TodayTileKind]}>();

// Which cell is which, so the observer's callback can name the tile it saw. Weak because a cell
// dropped from the grid should go with it.
const kindOf = new WeakMap<Element, TodayTileKind>();

const seen = useInViewOnce((cell) => {
  const kind = kindOf.get(cell);
  if (kind) {
    emit('tileSeen', kind);
  }
});

/**
 * Vue hands a function ref the element on mount and null on unmount, and calls it again on every
 * re-render. Both are handled downstream: the tracker ignores null and refuses an element it has
 * already reported.
 */
function watchCell(element: Element | null, kind: TodayTileKind): void {
  if (element) {
    kindOf.set(element, kind);
  }
  seen.watch(element);
}
</script>
<template>
  <div class="today-bento">
    <section
      v-for="row in rows"
      :key="row.id"
      class="today-bento__row"
      :data-row="row.id"
      :class="{
        'today-bento__row--multi': row.cells.length > 1,
        'today-bento__row--split': row.cells.length > 1 && row.cells.some((cell) => cell.span === 'full'),
      }"
      :aria-label="$t(`today_row_${row.id}`)"
      :style="{viewTransitionName: `today-row-${row.id}`}"
    >
      <div
        v-for="cell in row.cells"
        :key="cell.kind"
        :ref="(element) => watchCell(element as Element | null, cell.kind)"
        class="today-bento__cell"
        :class="`today-bento__cell--${cell.span}`"
        :data-tile="cell.kind"
      >
        <slot :name="cell.kind"/>
      </div>
    </section>
  </div>
</template>
<style scoped lang="scss">
// The bento measures its own width (the guide sidebar can shrink the content area at a
// fixed viewport), so column switches are container queries instead of media queries.
// Thresholds are the design's own steps, read off its layout: two columns from 640px of
// container width, three-column rows from 960px, where a wide tile also starts earning its
// extra column.
.today-bento {
  container: today-bento / inline-size;
  display: flex;
  flex-direction: column;
  gap: var(--today-grid-gap);
}

.today-bento__row {
  display: grid;
  gap: var(--today-grid-gap);
  grid-template-columns: minmax(0, 1fr);
}

// The grid owns the vertical rhythm. Cells in the same row already end up the same height
// without this: they are grid items, so they stretch to their track. What the floor adds is a
// minimum for the row as a whole, for the case where everything in it is short.
//
// A band that carries heights of its own supplies them through the generic pair
// --today-row-min-height-stacked / --today-row-min-height-multi, assigned per band in the token
// file next to the numbers and their measurements. The grid only picks WHICH of the pair applies
// from the shape the band is in; it never knows which band is which — composition knowledge
// stays in TODAY_ROW_CONFIG and the tokens, not in selectors here.
//
// The stacked value is the base case rather than the exception because a band is a single column
// in two situations, not one: below the column threshold, and whenever it holds a single cell at
// any width — `programs` holds one cell by construction, so the wider value is keyed on `--multi`
// below instead of on the container width alone.
.today-bento__cell {
  display: flex;
  min-width: 0;
  min-height: var(--today-row-min-height-stacked, var(--today-tile-min-height));

  > :deep(*) {
    flex: 1 1 auto;
    min-width: 0;
  }
}

// Not a band height but a tile's own, which is why it is keyed on the tile and not on the row: the
// assessment tile is the only one in its band that the design gives a constant height to, and its
// neighbour the journal is drawn at whatever its content comes to. See the token for the numbers.
.today-bento__cell[data-tile='assessment'] {
  min-height: var(--today-tile-min-height-assessment-stacked);
}

// There is deliberately no rule for the `full` span: the only full tile (`programs`) is alone in
// its band by construction, and a lone cell takes the row without help. A composition that put a
// full tile NEXT TO others would need explicit tracks again (the auto-flow rule below leaves no
// line to span across) — that composition does not exist, so the machinery for it was removed
// rather than shipped unreachable.

// Every multi-cell row splits in two at the same point, wide tile or not, which is where the
// design splits them. Rows with a wide tile used to hold out until 768px to avoid leaving a
// narrow sibling half-width beside a hole, but the no-holes rule below already covers that: a
// trailing odd cell stretches across instead. The wide tile earns its extra width at 960px.
@container today-bento (min-width: 640px) {
  .today-bento__row--multi {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  // `--multi` and not the container width alone: a row left with one visible tile is still a
  // single stacked column at this width, and keeps the stacked height above.
  .today-bento__row--multi .today-bento__cell {
    min-height: var(--today-row-min-height-multi, var(--today-row-min-height-stacked, var(--today-tile-min-height)));
  }

  .today-bento__row--multi .today-bento__cell[data-tile='assessment'] {
    min-height: var(--today-tile-min-height-assessment);
  }
}

// No-holes rule, scoped to the two-column band only: a trailing odd tile fills the row
// instead of sitting half-width next to an empty cell. Above 960px the row switches to
// one implicit track per cell, where the rule would create a phantom extra column.
//
// This started as our own call and turns out to be the design's too, which is worth knowing before
// anyone trades it away: measured in the prototype at 768px of window, the discover band renders
// two 324px cards and then stretches the third to the full 664px row rather than leaving it half
// width. Same behaviour, arrived at independently.
@container today-bento (min-width: 640px) and (max-width: 959.98px) {
  .today-bento__row--multi .today-bento__cell:last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }
}

@container today-bento (min-width: 960px) {
  // One implicit column track per rendered cell: dropped tiles reflow by construction.
  .today-bento__row--multi {
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
    grid-template-columns: none;
  }

  .today-bento__row--multi .today-bento__cell--wide {
    grid-column: span 2;
  }
}

// A row where one tile takes the whole line and the rest fall below it (IPD-3234: the Explore tile
// in its first-use state). Stated as its own rule rather than folded into the one above, because
// the column auto-flow there gives every cell a track of its own — a full cell in that row would
// simply become a wider column, never a line break. Rows are laid out again, so the full cell
// spans every track and the others flow onto the next line in pairs, which is the composition the
// design draws: the wide card, then the two content tiles side by side under it.
@container today-bento (min-width: 640px) {
  .today-bento__row--split {
    grid-auto-flow: row;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .today-bento__row--split .today-bento__cell--full {
    grid-column: 1 / -1;
  }

  // Below the full cell the payload leaves either two tiles or one — the content pair "can be ONE,
  // never assume length" — so both shapes are spelled out. A pair keeps half the row each, and a
  // lone one stretches rather than sitting half-width beside a hole, which is the same no-holes
  // rule the band above already follows.
  .today-bento__row--split .today-bento__cell:nth-child(3):last-child {
    grid-column: auto;
  }

  .today-bento__row--split .today-bento__cell--full + .today-bento__cell:last-child {
    grid-column: 1 / -1;
  }
}
</style>

<!-- Unscoped: the pseudo-elements a view transition animates hang off the document root, where a
     scoped attribute cannot reach them. -->
<style lang="scss">
// A row changes shape when the Explore tile earns the whole of it, and a grid span is not something
// that can be transitioned. The row is named rather than its tiles, so it is the row that is
// replaced — it goes and comes back in its new arrangement, rather than each card travelling to
// its new place and changing size on the way, which reads as the tile itself being stretched
// (IPD-3270). Every row carries a name, so the ones below it are carried to their new places
// rather than jumping there when the row above changes height.
::view-transition-group(*) {
  animation-duration: 420ms;
  animation-timing-function: cubic-bezier(0.32, 0.72, 0, 1);
}

@media (prefers-reduced-motion: reduce) {
  ::view-transition-group(*),
  ::view-transition-old(*),
  ::view-transition-new(*) {
    animation: none;
  }
}
</style>
